import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UserState {
  userId: string | null;
  email: string | null;
  displayName: string | null;
  timezone: string;
  onboardingCompleted: boolean;
  settings: {
    theme: 'light' | 'dark' | 'system';
    defaultView: 'day' | 'week' | 'month' | 'kanban' | 'table';
    accentColor: string;
    calendarDensity: 'compact' | 'balanced' | 'spacious';
    workHours: { start: string; end: string };
    workDays: number[];
    weekStartsOn: 0 | 1; // 0 = Sunday, 1 = Monday
    showGhostBlocks: boolean;
    enableNotifications: boolean;
    soundEnabled: boolean;
  };

  // WebSocket
  isConnected: boolean;
  connectedUsers: Array<{ userId: string; deviceId: string; view?: string }>;

  // Actions
  setUser: (user: {
    id: string;
    email: string;
    displayName?: string;
    timezone?: string;
    onboardingCompleted?: boolean;
    settings?: Partial<UserState['settings']>;
  }) => void;
  clearUser: () => void;
  updateSettings: (settings: Partial<UserState['settings']>) => void;
  setConnected: (connected: boolean) => void;
  setConnectedUsers: (users: UserState['connectedUsers']) => void;
}

const defaultSettings: UserState['settings'] = {
  theme: 'system',
  defaultView: 'week',
  accentColor: '#13D8BD',
  calendarDensity: 'balanced',
  workHours: { start: '09:00', end: '17:00' },
  workDays: [1, 2, 3, 4, 5],
  weekStartsOn: 0,
  showGhostBlocks: true,
  enableNotifications: true,
  soundEnabled: true,
};

export const useUserStore = create<UserState>()(
  persist(
    (set) => ({
      userId: null,
      email: null,
      displayName: null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      onboardingCompleted: false,
      settings: defaultSettings,
      isConnected: false,
      connectedUsers: [],

      setUser: (user) => {
        set(state => {
          const settings = normalizeSettings(user.settings ? { ...state.settings, ...user.settings } : state.settings);
          applyAppearance(settings);
          return {
            userId: user.id,
            email: user.email,
            displayName: user.displayName || null,
            timezone: user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            onboardingCompleted: user.onboardingCompleted ?? state.onboardingCompleted,
            settings,
          };
        });
      },

      clearUser: () => {
        set({
          userId: null,
          email: null,
          displayName: null,
          onboardingCompleted: false,
        });
      },

      updateSettings: (newSettings) => {
        set((state) => {
          const settings = normalizeSettings({ ...state.settings, ...newSettings });
          applyAppearance(settings);
          return { settings };
        });
      },

      setConnected: (connected) => set({ isConnected: connected }),
      setConnectedUsers: (users) => set({ connectedUsers: users }),
    }),
    {
      name: 'flexiplanner-user',
      partialize: (state) => ({
        userId: state.userId,
        email: state.email,
        displayName: state.displayName,
        timezone: state.timezone,
        onboardingCompleted: state.onboardingCompleted,
        settings: state.settings,
      }),
    }
  )
);

function normalizeSettings(settings: Partial<UserState['settings']>): UserState['settings'] {
  return {
    ...defaultSettings,
    ...settings,
    workHours: {
      ...defaultSettings.workHours,
      ...(settings.workHours || {}),
    },
    workDays: settings.workDays?.length ? settings.workDays : defaultSettings.workDays,
    accentColor: normalizeHex(settings.accentColor) || defaultSettings.accentColor,
    calendarDensity: ['compact', 'balanced', 'spacious'].includes(String(settings.calendarDensity))
      ? (settings.calendarDensity as UserState['settings']['calendarDensity'])
      : defaultSettings.calendarDensity,
  };
}

function normalizeHex(value?: string): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed.toUpperCase();
  if (/^[0-9a-f]{6}$/i.test(trimmed)) return `#${trimmed.toUpperCase()}`;
  return null;
}

function hexToHsl(value: string): string | null {
  const hex = normalizeHex(value);
  if (!hex) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const delta = max - min;
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    switch (max) {
      case r:
        h = (g - b) / delta + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / delta + 2;
        break;
      default:
        h = (r - g) / delta + 4;
        break;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyAppearance(settings: UserState['settings']) {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;
  const theme = settings.theme || defaultSettings.theme;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark);
  }

  const accent = normalizeHex(settings.accentColor) || defaultSettings.accentColor;
  const hsl = hexToHsl(accent);
  if (hsl) {
    root.style.setProperty('--primary', hsl);
    root.style.setProperty('--ring', hsl);
    root.style.setProperty('--planner-accent', accent);
  }
  root.dataset.density = settings.calendarDensity || defaultSettings.calendarDensity;
}

// Initialize appearance on load
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('flexiplanner-user');
  if (stored) {
    try {
      const { state } = JSON.parse(stored);
      applyAppearance(normalizeSettings(state?.settings || {}));
    } catch {}
  } else {
    applyAppearance(defaultSettings);
  }
}
